class CompareUtils {
  static cookieName = 'hypertheme:compare-products';
  static sessionProducts = [];
  static storageUnavailable = false;

  static productUrl(value) {
    try {
      const url = new URL(value, location.origin);
      const productPath = url.pathname.match(/\/products\/[^/]+\/?$/)?.[0];
      if (url.origin !== location.origin || !productPath) return null;
      const root = window.Shopify?.routes?.root || FoxTheme.routes.root_url || '/';
      const current = new URL(location.href);
      const result = new URL(root.replace(/\/$/, '') + productPath, location.origin);
      const variant = url.searchParams.get('variant');
      if (/^\d+$/.test(variant || '')) result.searchParams.set('variant', variant);
      for (const key of ['preview_theme_id', 'mm_stay_uk', 'mm_stay_us']) {
        if (current.searchParams.has(key)) result.searchParams.set(key, current.searchParams.get(key));
      }
      return result.pathname + result.search;
    } catch {
      return null;
    }
  }

  static normalize(products) {
    if (!Array.isArray(products)) return [];
    const seen = new Set();
    return products.flatMap((product) => {
      const id = String(product?.id || '');
      const url = this.productUrl(product?.url);
      if (!/^\d+$/.test(id) || !url || seen.has(id)) return [];
      seen.add(id);
      return [{ id, url }];
    }).slice(0, FoxTheme.compare.maxProductsInCompare);
  }

  static getSelectedProducts() {
    if (!this.storageUnavailable && FoxTheme.config.hasLocalStorage) {
      try {
        this.sessionProducts = this.normalize(FoxTheme.utils.getStorage(this.cookieName) || []);
      } catch {
        this.storageUnavailable = true;
      }
    }
    return this.normalize(this.sessionProducts);
  }

  static setSelectedProducts(products) {
    this.sessionProducts = this.normalize(products);
    if (!this.storageUnavailable && FoxTheme.config.hasLocalStorage) {
      try {
        if (this.sessionProducts.length) FoxTheme.utils.setStorage(this.cookieName, this.sessionProducts);
        else localStorage.removeItem(this.cookieName);
      } catch {
        this.storageUnavailable = true;
      }
    }
  }

  static getProductsCount() {
    return this.getSelectedProducts().length;
  }

  static isProductInList(id) {
    return this.getSelectedProducts().some((product) => product.id === String(id));
  }

  static addToList(id, productUrl) {
    const products = this.getSelectedProducts();
    if (this.isProductInList(id)) return true;
    const product = this.normalize([{ id, url: productUrl }])[0];
    if (!product) return false;
    if (products.length >= FoxTheme.compare.maxProductsInCompare) {
      alert(FoxTheme.compare.alertMessage);
      return false;
    }
    products.push(product);
    this.setSelectedProducts(products);
    this.updateCompareCheckboxes();
    FoxTheme.pubsub.publish('compare:add', { products, productId: product.id, productUrl: product.url });
    return true;
  }

  static removeFromList(id) {
    const productId = String(id);
    const products = this.getSelectedProducts().filter((product) => product.id !== productId);
    this.setSelectedProducts(products);
    this.updateCompareCheckboxes();
    FoxTheme.pubsub.publish('compare:remove', { products, productId });
  }

  static clearList() {
    this.getSelectedProducts().forEach((product) => this.removeFromList(product.id));
  }

  static updateCompareCheckboxes() {
    const selected = new Set(this.getSelectedProducts().map((product) => product.id));
    document.querySelectorAll('.js-compare-checkbox').forEach((checkbox) => {
      checkbox.checked = selected.has(checkbox.dataset.productId);
    });
  }

  static getTemplateUrl(productUrl, query = {}) {
    const path = this.productUrl(productUrl);
    if (!path) throw new Error('Invalid product path');
    const url = new URL(path, location.origin);
    Object.entries(query).forEach(([key, value]) => url.searchParams.set(key, value));
    return url.pathname + url.search;
  }

  static async fetchTemplate(product, sectionId, bar, signal) {
    const controller = new AbortController();
    const abort = () => controller.abort();
    if (signal?.aborted) abort();
    signal?.addEventListener('abort', abort, { once: true });
    const timer = setTimeout(abort, 10000);
    try {
      const response = await fetch(this.getTemplateUrl(product.url, {
        [bar ? 'sections' : 'section_id']: sectionId
      }), { signal: controller.signal, credentials: 'same-origin' });
      if (!response.ok) {
        const error = new Error('Compare response unavailable');
        error.unavailable = response.status === 404 || response.status === 410;
        throw error;
      }
      const content = bar ? (await response.json())[sectionId] : await response.text();
      if (typeof content !== 'string') throw new Error('Compare section missing');
      const doc = new DOMParser().parseFromString(content, 'text/html');
      const element = bar
        ? doc.querySelector(`.compare-bar__item[data-product-id="${product.id}"]`)
        : doc.querySelector(`#MainProduct-compare__rows[data-product-id="${product.id}"]`);
      if (!element) throw new Error('Compare product missing');
      return element;
    } finally {
      clearTimeout(timer);
      signal?.removeEventListener('abort', abort);
    }
  }

  static errorItem(product, error, tag = 'li') {
    const item = document.createElement(tag);
    item.dataset.productId = product.id;
    item.dataset.compareError = 'true';
    item.className = 'compare-bar__item compare-load-error';
    const link = document.createElement('a');
    link.href = product.url;
    link.textContent = error.unavailable ? FoxTheme.compare.unavailable : FoxTheme.compare.error;
    item.append(link);
    const remove = document.createElement('button', { is: 'compare-remove' });
    remove.setAttribute('is', 'compare-remove');
    remove.dataset.productId = product.id;
    remove.type = 'button';
    remove.className = 'btn btn--link';
    remove.textContent = FoxTheme.compare.remove;
    remove.setAttribute('aria-label', `${FoxTheme.compare.remove}: ${product.url.split('?')[0].split('/').pop()}`);
    item.append(remove);
    return item;
  }
}

class CompareSwitch extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    this.checkboxEl = this.querySelector('input');
    let active = false;
    if (FoxTheme.config.hasLocalStorage) {
      try { active = localStorage.getItem('hypertheme:compare-is-active') === 'true'; } catch {}
    }
    this.checkboxEl.checked = active;
    this.handleChange(active);
    this.checkboxEl.addEventListener('change', () => this.handleChange(this.checkboxEl.checked), {
      signal: this.abortController.signal
    });
  }

  handleChange(active) {
    document.body.classList.toggle('is-product-comparing', active);
    FoxTheme.pubsub.publish('compare:toggle', { isActive: active });
    if (FoxTheme.config.hasLocalStorage) {
      try { localStorage.setItem('hypertheme:compare-is-active', active); } catch {}
    }
  }

  disconnectedCallback() { this.abortController?.abort(); }
}
customElements.define('compare-switch', CompareSwitch);

class CompareCheckbox extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    this.checkboxEl = this.querySelector('input');
    this.checkboxEl.checked = CompareUtils.isProductInList(this.checkboxEl.dataset.productId);
    this.checkboxEl.addEventListener('change', () => {
      const { productId, productUrl } = this.checkboxEl.dataset;
      if (this.checkboxEl.checked) this.checkboxEl.checked = CompareUtils.addToList(productId, productUrl);
      else CompareUtils.removeFromList(productId);
    }, { signal: this.abortController.signal });
  }

  disconnectedCallback() { this.abortController?.abort(); }
}
customElements.define('compare-checkbox', CompareCheckbox);

class CompareRemove extends HTMLButtonElement {
  connectedCallback() {
    this.abortController = new AbortController();
    this.addEventListener('click', (event) => {
      event.preventDefault();
      const drawer = this.closest('compare-drawer');
      const wasFocused = document.activeElement === this;
      const id = this.dataset.productId;
      CompareUtils.removeFromList(id);
      if (drawer) {
        drawer.querySelectorAll(`[data-dynamic-product="${id}"]`).forEach((cell) => cell.remove());
        drawer.updateProductsCount();
        if (CompareUtils.getProductsCount() < 2) {
          drawer.activeElement = document.querySelector('#ProductCompareSwitch') || document.querySelector('.compare-bar__clear');
          drawer.hide();
        } else if (wasFocused) {
          (drawer.querySelector('[is="compare-remove"]') || drawer.focusElement).focus();
        }
        drawer.syncFocus();
      } else if (wasFocused) {
        (document.querySelector('#ProductCompareSwitch') || document.querySelector('.compare-bar__clear'))?.focus();
      }
    }, { signal: this.abortController.signal });
  }

  disconnectedCallback() { this.abortController?.abort(); }
}
customElements.define('compare-remove', CompareRemove, { extends: 'button' });

class CompareBar extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    this.list = this.querySelector('.compare-bar__list');
    this.toggle = this.querySelector('.compare-bar__toggle-list');
    this.counter = this.querySelector('.compare-bar__counter');
    this.openButton = this.querySelector('.compare-bar__open');
    this.status = this.querySelector('.compare-bar__status');
    this.querySelectorAll('.compare-bar__clear').forEach((button) => {
      button.addEventListener('click', () => CompareUtils.clearList(), { signal: this.abortController.signal });
    });
    this.toggle.addEventListener('click', () => {
      const expanded = this.classList.toggle('is-show-list');
      this.toggle.setAttribute('aria-expanded', String(expanded));
      this.setHeight();
    }, { signal: this.abortController.signal });
    this.itemAddUnsubscriber = FoxTheme.pubsub.subscribe('compare:add', () => this.renderBar());
    this.itemRemoveUnsubscriber = FoxTheme.pubsub.subscribe('compare:remove', () => this.renderBar());
    this.mediaQuery = matchMedia(FoxTheme.config.mediaQueryMobile);
    this.onResize = () => this.setHeight();
    this.mediaQuery.addEventListener('change', this.onResize);
    this.inert = !document.body.classList.contains('is-product-comparing');
    this.compareToggleUnsubscriber = FoxTheme.pubsub.subscribe('compare:toggle', ({ isActive }) => {
      this.inert = !isActive;
    });
    this.renderBar();
  }

  async renderBar() {
    this.fetchController?.abort();
    this.fetchController = new AbortController();
    const generation = this.generation = (this.generation || 0) + 1;
    const products = CompareUtils.getSelectedProducts();
    const currentItems = new Map([...this.list.querySelectorAll('.compare-bar__item[data-product-id]:not([data-compare-error])')].map((item) => [item.dataset.productId, item]));
    this.openButton.disabled = products.length < 2;
    this.counter.textContent = products.length;
    this.status.textContent = `${FoxTheme.compare.label}: ${products.length}/${FoxTheme.compare.maxProductsInCompare}`;
    const items = await Promise.all(products.map(async (product) => {
      if (currentItems.has(product.id)) return currentItems.get(product.id);
      try { return await CompareUtils.fetchTemplate(product, 'product-compare-bar-item', true, this.fetchController.signal); }
      catch (error) { return CompareUtils.errorItem(product, error); }
    }));
    if (!this.isConnected || generation !== this.generation) return;
    this.list.replaceChildren(...items);
    if (items.some((item) => item.dataset.compareError)) this.status.textContent += ` — ${FoxTheme.compare.error}`;
    for (let i = products.length; i < Math.min(5, FoxTheme.compare.maxProductsInCompare); i++) {
      const placeholder = document.createElement('li');
      placeholder.className = 'compare-bar__item compare-bar__item-placeholder';
      placeholder.setAttribute('aria-hidden', 'true');
      this.list.append(placeholder);
    }
    this.setHeight();
  }

  setHeight() {
    requestAnimationFrame(() => {
      if (this.isConnected) document.documentElement.style.setProperty('--compare-bar-height', `${Math.round(this.offsetHeight)}px`);
    });
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.fetchController?.abort();
    this.itemAddUnsubscriber?.();
    this.itemRemoveUnsubscriber?.();
    this.compareToggleUnsubscriber?.();
    this.mediaQuery?.removeEventListener('change', this.onResize);
  }
}
customElements.define('compare-bar', CompareBar);

class CompareDrawer extends DrawerComponent {
  get requiresBodyAppended() { return false; }

  connectedCallback() {
    super.connectedCallback();
    this.scrollRegion = this.querySelector('.compare-table-container');
    this.scrollButtons = [...this.querySelectorAll('[data-compare-scroll]')];
    this.scrollButtons.forEach((button) => button.addEventListener('click', () => {
      const column = this.querySelector('th[data-dynamic-product]');
      const step = column?.getBoundingClientRect().width || 200;
      this.scrollRegion.scrollBy({ left: Number(button.dataset.compareScroll) * step * (FoxTheme.config.isRTL ? -1 : 1), behavior: FoxTheme.config.motionReduced ? 'instant' : 'smooth' });
    }, { signal: this.abortController.signal }));
    this.scrollRegion.addEventListener('scroll', () => this.updateScrollButtons(), { signal: this.abortController.signal });
    this.resizeObserver = new ResizeObserver(() => this.updateScrollButtons());
    this.resizeObserver.observe(this.scrollRegion);
    this.querySelector('.compare-refresh').addEventListener('click', () => {
      document.querySelector('compare-bar')?.renderBar();
      this.renderCompareTable();
    }, {
      signal: this.abortController.signal
    });
    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab' || !this.open) return;
      const controls = this.visibleControls();
      const target = event.shiftKey ? controls.at(-1) : controls[0];
      if (controls.length && ((event.shiftKey && event.target === controls[0]) || (!event.shiftKey && event.target === controls.at(-1)))) {
        event.preventDefault();
        event.stopImmediatePropagation();
        target.focus();
      }
    }, { signal: this.abortController.signal, capture: true });
  }

  visibleControls() {
    if (this.classList.contains('drawer--loading')) return [this.focusElement];
    return FoxTheme.a11y.getFocusableElements(this).filter((element) =>
      !element.closest('[hidden],[inert]') && element.getClientRects().length &&
      getComputedStyle(element).visibility !== 'hidden'
    );
  }

  syncFocus() {
    if (!this.open) return;
    const controls = this.visibleControls();
    const target = controls.includes(document.activeElement) ? document.activeElement : this.focusElement;
    FoxTheme.a11y.trapFocus(this, target);
    FoxTheme.a11y.trapFocusHandlers.focusin({ target });
  }

  handleAfterShow() {
    super.handleAfterShow();
    this.updateScrollButtons();
    this.syncFocus();
  }

  updateScrollButtons() {
    const max = this.scrollRegion.scrollWidth - this.scrollRegion.clientWidth;
    const position = Math.abs(this.scrollRegion.scrollLeft);
    let changed = false;
    this.scrollButtons.forEach((button) => {
      const disabled = Number(button.dataset.compareScroll) < 0 ? position < 1 : position >= max - 1;
      changed ||= button.disabled !== disabled;
      button.disabled = disabled;
    });
    this.querySelector('.compare-scroll-controls').hidden = max <= 1;
    if (this.open && changed && this.scrollButtons.includes(document.activeElement) && document.activeElement.disabled) {
      (this.scrollButtons.find((button) => !button.disabled) || this.focusElement).focus();
      this.syncFocus();
    }
  }

  getSectionId() {
    return FoxTheme.utils.getSectionId(this.closest('.shopify-section'));
  }

  prepareToShow() {
    super.prepareToShow();
    this.renderCompareTable();
  }

  prepareToHide() {
    super.prepareToHide();
    this.fetchController?.abort();
    this.generation = (this.generation || 0) + 1;
  }

  updateProductsCount() {
    this.style.setProperty('--number-items', Math.max(2, CompareUtils.getProductsCount()));
  }

  async renderCompareTable() {
    this.fetchController?.abort();
    this.fetchController = new AbortController();
    const generation = this.generation = (this.generation || 0) + 1;
    const signal = this.fetchController.signal;
    const products = CompareUtils.getSelectedProducts();
    this.updateProductsCount();
    this.classList.add('drawer--loading');
    this.setAttribute('aria-busy', 'true');
    this.syncFocus();
    const rows = [...this.querySelectorAll('.compare-table tr[data-compare-key]')];
    const keys = rows.map((row) => row.dataset.compareKey);
    const results = await Promise.all(products.map(async (product) => {
      try {
        const template = await CompareUtils.fetchTemplate(product, this.getSectionId(), false, signal);
        const fragment = template.content.cloneNode(true);
        if (!keys.every((key) => fragment.querySelector(`[data-compare-key="${key}"]`))) throw new Error('Compare cells missing');
        return { product, fragment };
      } catch (error) { return { product, error }; }
    }));
    if (!this.isConnected || generation !== this.generation) return;
    this.querySelectorAll('[data-dynamic-product]').forEach((cell) => cell.remove());
    const selected = new Set(CompareUtils.getSelectedProducts().map((product) => product.id));
    let failed = false;
    results.filter(({ product }) => selected.has(product.id)).forEach(({ product, fragment, error }) => {
      failed ||= Boolean(error);
      rows.forEach((row) => {
        const key = row.dataset.compareKey;
        let cell = fragment?.querySelector(`[data-compare-key="${key}"]`);
        if (!cell) {
          cell = document.createElement(key === 'info' ? 'th' : 'td');
          if (key === 'info') {
            const item = CompareUtils.errorItem(product, error, 'div');
            item.classList.remove('compare-bar__item');
            cell.append(item);
          } else cell.textContent = '—';
        }
        cell.dataset.dynamicProduct = product.id;
        if (key === 'info') {
          if (cell.tagName !== 'TH') {
            const heading = document.createElement('th');
            heading.dataset.dynamicProduct = product.id;
            heading.append(...cell.childNodes);
            cell = heading;
          }
          cell.id = `CompareProduct-${product.id}`;
          cell.scope = 'col';
        } else cell.setAttribute('headers', `CompareRow-${key} CompareProduct-${product.id}`);
        row.append(cell);
      });
    });
    const status = this.querySelector('.compare-drawer__status');
    status.textContent = failed ? FoxTheme.compare.error : `${FoxTheme.compare.label}: ${selected.size}`;
    this.querySelector('.compare-refresh').hidden = !failed;
    this.querySelector('.compare-refresh').disabled = !failed;
    this.classList.remove('drawer--loading');
    this.setAttribute('aria-busy', 'false');
    this.updateScrollButtons();
    this.syncFocus();
  }

  handleAfterHide() {
    super.handleAfterHide();
    this.querySelectorAll('[data-dynamic-product]').forEach((cell) => cell.remove());
    this.classList.add('drawer--loading');
  }

  disconnectedCallback() {
    this.fetchController?.abort();
    this.resizeObserver?.disconnect();
    this.generation = (this.generation || 0) + 1;
    super.disconnectedCallback();
  }
}
customElements.define('compare-drawer', CompareDrawer);
